import type { AssessmentResponse } from '@/lib/types';
import { inlineMarkdown } from '@/lib/markdown';

interface AssessmentFeedbackProps {
  responses: AssessmentResponse[];
  overallScore: number;
  correctCount: number;
  totalQuestions: number;
  onContinue: () => void;
}

export function AssessmentFeedback({
  responses,
  overallScore,
  correctCount,
  totalQuestions,
  onContinue,
}: AssessmentFeedbackProps) {
  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 dark:text-green-400';
    if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getScoreMessage = (score: number) => {
    if (score >= 80) return 'Excellent work! 🎉';
    if (score >= 60) return 'Good effort! 👍';
    return 'Keep learning! 📚';
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Overall Score Card */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-8 text-center">
        <h2 className="text-2xl font-serif font-bold mb-4">Assessment Complete!</h2>

        <div className={`text-6xl font-bold mb-2 ${getScoreColor(overallScore)}`}>
          {overallScore}%
        </div>

        <p className="text-lg text-slate-600 dark:text-slate-400 mb-2">
          {correctCount} out of {totalQuestions} correct
        </p>

        <p className="text-xl font-medium">{getScoreMessage(overallScore)}</p>
      </div>

      {/* Individual Question Feedback */}
      <div className="space-y-4">
        <h3 className="text-xl font-serif font-semibold">Question Feedback</h3>

        {responses.map((response, idx) => (
          <div
            key={response.questionId}
            className={`
              bg-white dark:bg-slate-800 rounded-xl shadow p-6
              border-l-4
              ${
                response.isCorrect
                  ? 'border-green-500'
                  : 'border-red-500'
              }
            `}
          >
            <div className="flex items-start justify-between mb-3">
              <h4 className="font-medium text-slate-900 dark:text-slate-100">
                Question {idx + 1}
              </h4>
              <span
                className={`
                  px-3 py-1 rounded-full text-sm font-medium
                  ${
                    response.isCorrect
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                      : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                  }
                `}
              >
                {response.isCorrect ? '✓ Correct' : '✗ Incorrect'}
              </span>
            </div>

            {response.feedback && (
              <div className="text-sm text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-900/50 rounded-lg p-4"
                   dangerouslySetInnerHTML={{ __html: inlineMarkdown(response.feedback) }} />
            )}
          </div>
        ))}
      </div>

      {/* Continue Button */}
      <div className="text-center pt-4">
        <button
          onClick={onContinue}
          className="px-8 py-3 accent-bg hover:opacity-90 active:scale-[0.98] text-white font-medium rounded-lg transition-all"
        >
          Continue to Reflection →
        </button>
      </div>
    </div>
  );
}
